import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../App";
import { ArrowLeft, ArrowRight, Briefcase, Home, Heart, DollarSign, GraduationCap, Globe } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import axios from "axios";

const categories = [
  { id: "trabajo", label: "Trabajo", icon: Briefcase, color: "#002FA7" },
  { id: "vivienda", label: "Vivienda", icon: Home, color: "#5B21B6" },
  { id: "relaciones", label: "Relaciones", icon: Heart, color: "#EC4899" },
  { id: "finanzas", label: "Finanzas", icon: DollarSign, color: "#10B981" },
  { id: "educacion", label: "Educación", icon: GraduationCap, color: "#F59E0B" },
  { id: "otro", label: "Otro", icon: Globe, color: "#6B7280" },
];

const NewDecision = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      setError("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API}/decisions`,
        {
          title: title.trim(),
          description: description.trim(),
          category: category || "general"
        },
        { withCredentials: true }
      );

      navigate(`/simulation/${response.data.decision_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al crear la decisión");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5EA]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6B7280] hover:text-[#111111] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-lg text-[#111111]">FUTURA</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="animate-fade-in">
          <div className="text-center mb-10">
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#111111] tracking-tight mb-3">
              Describe tu decisión
            </h1>
            <p className="text-[#6B7280] max-w-lg mx-auto">
              Cuéntanos qué estás considerando y te guiaremos a través de un análisis completo
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Category Selection */}
            <div className="space-y-4">
              <Label className="text-[#111111] font-medium text-base">
                ¿En qué área de tu vida?
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                      category === cat.id
                        ? "border-[#002FA7] bg-[#002FA7]/5"
                        : "border-[#E5E5EA] bg-white hover:border-[#002FA7]/50"
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${cat.color}15` }}
                    >
                      <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                    </div>
                    <span className={`text-xs font-medium ${
                      category === cat.id ? "text-[#002FA7]" : "text-[#6B7280]"
                    }`}>
                      {cat.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-3">
              <Label htmlFor="title" className="text-[#111111] font-medium text-base">
                ¿Cuál es la decisión?
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Cambiar de trabajo, mudarme a otra ciudad..."
                className="h-14 bg-white border-[#E5E5EA] rounded-2xl text-lg focus:border-[#002FA7] focus:ring-[#002FA7]"
                required
                data-testid="decision-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-3">
              <Label htmlFor="description" className="text-[#111111] font-medium text-base">
                Cuéntanos más detalles
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe tu situación actual, qué opciones estás considerando, y cualquier detalle relevante que nos ayude a analizar mejor tu caso..."
                className="min-h-[150px] bg-white border-[#E5E5EA] rounded-2xl text-base focus:border-[#002FA7] focus:ring-[#002FA7] resize-none"
                required
                data-testid="decision-description-input"
              />
              <p className="text-sm text-[#6B7280]">
                Cuanta más información proporciones, más precisa será la simulación
              </p>
            </div>

            {error && (
              <div className="p-4 bg-[#EF4444]/10 text-[#EF4444] rounded-xl text-sm" data-testid="new-decision-error">
                {error}
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={loading || !title.trim() || !description.trim()}
                className="bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-full px-8 py-6 text-lg btn-lift"
                data-testid="start-simulation-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    Comenzar simulación
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewDecision;
